import { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { Badge } from './ui/badge';
import { X, Search, Loader2, Refrigerator, Sparkles } from 'lucide-react';

const IngredientAutocomplete = ({ 
    selectedIngredients = [], 
    onIngredientsChange,
    placeholder = "Tapez un ingrédient...",
    maxSelections = 10,
    className = "",
    showMatchMode = false,
    matchMode = "any",
    onMatchModeChange = null
}) => {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const inputRef = useRef(null);
    const containerRef = useRef(null);

    // Debounced search for suggestions
    useEffect(() => {
        const timer = setTimeout(async () => {
            if (query.length >= 2) {
                setLoading(true);
                try {
                    const response = await api.getIngredientsAutocomplete(query);
                    // Filter out already selected ingredients
                    const filtered = response.data.ingredients.filter(
                        ing => !selectedIngredients.some(
                            sel => sel.toLowerCase() === ing.toLowerCase()
                        )
                    );
                    setSuggestions(filtered);
                    setShowSuggestions(true);
                } catch (error) {
                    console.error('Autocomplete error:', error);
                    setSuggestions([]);
                }
                setLoading(false);
            } else {
                setSuggestions([]);
                setShowSuggestions(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [query, selectedIngredients]);

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const addIngredient = (ingredient) => {
        if (selectedIngredients.length < maxSelections) {
            const newIngredients = [...selectedIngredients, ingredient];
            onIngredientsChange(newIngredients);
            setQuery('');
            setSuggestions([]);
            setShowSuggestions(false);
            inputRef.current?.focus();
        }
    };

    const removeIngredient = (ingredient) => {
        const newIngredients = selectedIngredients.filter(i => i !== ingredient);
        onIngredientsChange(newIngredients);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && query.trim()) {
            e.preventDefault();
            // Add custom ingredient if no suggestion matches exactly
            if (suggestions.length > 0) {
                addIngredient(suggestions[0]);
            } else if (query.trim().length >= 2) {
                addIngredient(query.trim());
            }
        }
        if (e.key === 'Escape') {
            setShowSuggestions(false);
        }
    };

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            {/* Selected Ingredients */}
            {selectedIngredients.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3 p-3 bg-green-50 rounded-lg border border-green-200">
                    {selectedIngredients.map(ingredient => (
                        <Badge 
                            key={ingredient} 
                            variant="secondary" 
                            className="bg-green-100 text-green-800 gap-1 py-1"
                        >
                            {ingredient}
                            <X 
                                className="w-3 h-3 cursor-pointer hover:text-red-500" 
                                onClick={() => removeIngredient(ingredient)}
                            />
                        </Badge>
                    ))}
                </div>
            )}

            {/* Input Field */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => query.length >= 2 && setShowSuggestions(true)}
                    placeholder={placeholder}
                    disabled={selectedIngredients.length >= maxSelections}
                    className="w-full pl-10 pr-10 py-2.5 border-2 rounded-xl focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                    data-testid="ingredient-autocomplete-input"
                />
                {loading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
                )}
            </div>

            {/* Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border-2 border-foreground rounded-xl shadow-lg max-h-48 overflow-y-auto">
                    {suggestions.map((suggestion, idx) => (
                        <button
                            key={suggestion}
                            onClick={() => addIngredient(suggestion)}
                            className={`w-full px-4 py-2.5 text-left hover:bg-green-50 transition-colors ${
                                idx === 0 ? 'rounded-t-lg' : ''
                            } ${idx === suggestions.length - 1 ? 'rounded-b-lg' : ''}`}
                            data-testid={`suggestion-${idx}`}
                        >
                            <span className="font-medium">{suggestion}</span>
                        </button>
                    ))}
                </div>
            )}

            {/* Match Mode Toggle - "Videz votre frigo" */}
            {showMatchMode && selectedIngredients.length > 0 && onMatchModeChange && (
                <div className="mt-3 flex gap-2">
                    <button
                        onClick={() => onMatchModeChange("any")}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl border-2 text-sm font-medium transition-all ${
                            matchMode === "any"
                                ? "border-green-500 bg-green-100 text-green-800"
                                : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                        }`}
                        data-testid="match-mode-any"
                    >
                        <Search className="w-4 h-4" />
                        Au moins un
                    </button>
                    <button
                        onClick={() => onMatchModeChange("max")}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl border-2 text-sm font-medium transition-all ${
                            matchMode === "max"
                                ? "border-purple-500 bg-purple-100 text-purple-800"
                                : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                        }`}
                        data-testid="match-mode-max"
                    >
                        <Refrigerator className="w-4 h-4" />
                        Videz votre frigo
                    </button>
                </div>
            )}

            {/* Mode explanation */}
            {showMatchMode && selectedIngredients.length > 0 && matchMode === "max" && (
                <p className="text-xs text-purple-600 mt-2 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    Les recettes utilisant le plus d'ingrédients apparaîtront en premier
                </p>
            )}

            {/* Helper Text */}
            {selectedIngredients.length >= maxSelections && (
                <p className="text-sm text-muted-foreground mt-2">
                    Maximum {maxSelections} ingrédients atteint
                </p>
            )}
        </div>
    );
};

export default IngredientAutocomplete;
